import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { LogoutService, LogoutReason } from './logout.service';
import { BroadcastChannelService } from '../broadcast-channel.service';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AuthExchangeResponse } from './auth-exchange.model';
import {
  ACCESS_TOKEN_KEY,
  AUTH_EXCHANGE_PATH,
  AUTH_REFRESH_PATH,
  INACTIVITY_TIMEOUT_MS,
  REFRESH_TOKEN_KEY
} from './auth.constants';
import { ModernFrameSessionService } from '../../core/modern-host/modern-frame-session.service';
import { PageAccessService } from './page-access.service';
import { ActivityService } from './activityService.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  readonly #activity = inject(ActivityService);
  readonly #modernFrameSession = inject(ModernFrameSessionService);
  readonly #pageAccess = inject(PageAccessService);
  readonly #activitySubscription: Subscription;
  #expirationTimeoutId?: ReturnType<typeof setTimeout>;
  private loginCompleted = new BehaviorSubject<boolean>(false);
  loginCompleted$ = this.loginCompleted.asObservable();
  userEmail: any;
  #azureIdToken: string | null = null;
  #loginInFlight$: Observable<boolean> | null = null;
  #refreshInFlight$: Observable<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private logoutService: LogoutService,
    private broadcastChannelService: BroadcastChannelService
  ) {
    this.#activitySubscription = this.#activity.activity$.subscribe(() => this.#resetExpirationTimer());
    this.startMonitoring();
  }

  login(userEmail: string, azureIdToken: string): Observable<boolean> {
    if (this.#loginInFlight$) {
      return this.#loginInFlight$;
    }

    this.userEmail = userEmail;
    this.loginCompleted.next(false);

    const url = `${environment.urlProfileManagement}${AUTH_EXCHANGE_PATH}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${azureIdToken}`,
      byPass: 'true'
    });

    this.#loginInFlight$ = this.http.post<AuthExchangeResponse>(url, {}, { headers }).pipe(
      map((response) => {
        this.#persistTokens(response);
        this.#resetExpirationTimer();
        this.loginCompleted.next(true);
        this.#pageAccess.invalidateSession();
        this.#modernFrameSession.notifySessionReplaced();
        return true;
      }),
      catchError(() => {
        this.loginCompleted.next(false);
        return of(false);
      }),
      finalize(() => {
        this.#loginInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.#loginInFlight$;
  }

  setAzureIdToken(idToken: string): void {
    this.#azureIdToken = idToken;
  }

  getAzureIdToken(): string | null {
    return this.#azureIdToken;
  }

  clearAzureIdToken(): void {
    this.#azureIdToken = null;
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  restoreSessionIfValid(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    if (!this.loginCompleted.getValue()) {
      this.#pageAccess.invalidateSession();
      this.#modernFrameSession.notifySessionReplaced();
      this.loginCompleted.next(true);
      this.#resetExpirationTimer();
    }

    return true;
  }

  refreshTokens(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return of(false);
    }

    if (this.#refreshInFlight$) {
      return this.#refreshInFlight$;
    }

    const url = `${environment.urlProfileManagement}${AUTH_REFRESH_PATH}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${refreshToken}`,
      byPass: 'true'
    });

    this.#refreshInFlight$ = this.http.post<AuthExchangeResponse>(url, {}, { headers }).pipe(
      map((response) => {
        this.#persistTokens(response);
        return true;
      }),
      catchError(() => of(false)),
      finalize(() => {
        this.#refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.#refreshInFlight$;
  }

  #persistTokens(response: AuthExchangeResponse): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
  }

  private clearExpirationTimer(): void {
    if (this.#expirationTimeoutId !== undefined) {
      clearTimeout(this.#expirationTimeoutId);
      this.#expirationTimeoutId = undefined;
    }
  }

  logout(reason: LogoutReason = 'manual', skipBroadcast: boolean = false): void {
    if (!this.logoutService.beginLogout(reason)) {
      return;
    }

    const currentUrl = this.router.url || window.location.pathname + window.location.search + window.location.hash;

    try {
      if (reason !== 'manual') {
        this.logoutService.setReturnUrl(currentUrl);
        if (!skipBroadcast) {
          this.broadcastChannelService.notifyLogoutInitiated(reason, currentUrl);
        }
      } else {
        this.logoutService.clearReturnUrl();
        if (!skipBroadcast) {
          this.broadcastChannelService.notifyLogoutInitiated(reason, null);
        }
      }
    } catch (error) {
      console.warn('Could not publish logout state', error);
    }

    this.#modernFrameSession.notifyLogout(reason);
    this.#pageAccess.invalidateSession();
    this.loginCompleted.next(false);
    this.clearExpirationTimer();
    this.#loginInFlight$ = null;
    this.#refreshInFlight$ = null;
    this.clearAzureIdToken();

    this.logoutService.completeLogout();
  }

  startMonitoring(): void {
    this.#resetExpirationTimer();
  }

  private checkActivity(): void {
    if (!this.getAccessToken()) {
      return;
    }

    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    const currentTime = new Date().getTime();
    if (lastActivity === 0 || currentTime - lastActivity > INACTIVITY_TIMEOUT_MS) {
      this.logout('inactive');
      return;
    }
    this.#resetExpirationTimer();
  }

  #resetExpirationTimer(): void {
    this.clearExpirationTimer();
    if (!this.getAccessToken()) return;

    const lastActivity = Number(localStorage.getItem('lastActivity') ?? 0);
    const elapsed = lastActivity > 0 ? Date.now() - lastActivity : INACTIVITY_TIMEOUT_MS;
    const remaining = Math.max(0, INACTIVITY_TIMEOUT_MS - elapsed);
    this.#expirationTimeoutId = setTimeout(() => this.checkActivity(), remaining);
  }

  ngOnDestroy(): void {
    this.clearExpirationTimer();
    this.#activitySubscription.unsubscribe();
  }
}
